import React, { useState } from 'react';
import { CheckCircle2, Printer } from 'lucide-react';
import { Button } from '../../Button';
import { ModalShell } from '../../cockpit/CockpitBits';
import { dateiAusgeben } from '../../../services/dateiAusgabe';
import { describeError } from '../../../services/errorMessage';
import {
    KARTEN_DRUCKARTEN,
    KARTEN_DRUCKART_VORGABE,
    KARTEN_FORMATE,
    KARTEN_FORMAT_VORGABE,
    bogenText,
    kartenDateiname,
    type KartenWahl,
} from '../../../services/material/materialLogic';
import type { KartenDruckart, KartenEingabe, KartenFormat } from '../../../services/material/materialTypes';

/**
 * „Karten drucken“: Format und Druckart wählen, PDF erzeugen und ausgeben —
 * im Browser als Download, in der Android-App über den Teilen-Dialog
 * (`dateiAusgeben`). Der PDF-Generator (jsPDF, QR, Schrift) wird erst beim
 * Klick geladen (Muster gbuAusgabe).
 *
 * Die QR-Codes zeigen auf die Adresse, unter der die App gerade läuft — eine
 * Karte aus TEST meldet in TEST. Der Dialog nennt sie deshalb.
 */

interface Props {
    karten: KartenEingabe[];
    onSchliessen: () => void;
}

/** So viele Namen nennt die Zusammenfassung, der Rest als „+ n weitere“. */
const NAMEN_MAX = 6;

function Wahl<T extends string>({ name, titel, wahlen, wert, gesperrt, onWahl }: {
    name: string;
    titel: string;
    wahlen: KartenWahl<T>[];
    wert: T;
    gesperrt: boolean;
    onWahl: (w: T) => void;
}) {
    return (
        <fieldset className="space-y-2" disabled={gesperrt}>
            <legend className="block text-[11px] font-bold text-slate-500 uppercase mb-1">{titel}</legend>
            {wahlen.map(w => (
                <label
                    key={w.wert}
                    className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                        wert === w.wert ? 'border-blue-300 bg-blue-50/60' : 'border-slate-200 hover:bg-slate-50'
                    }`}
                >
                    <input
                        type="radio"
                        name={name}
                        value={w.wert}
                        checked={wert === w.wert}
                        onChange={() => onWahl(w.wert)}
                        className="mt-1 shrink-0"
                    />
                    <span className="min-w-0">
                        <span className="block text-sm font-semibold text-slate-800">{w.titel}</span>
                        <span className="block text-xs text-slate-500 leading-relaxed">{w.erklaerung}</span>
                    </span>
                </label>
            ))}
        </fieldset>
    );
}

export const KartenDruckDialog: React.FC<Props> = ({ karten, onSchliessen }) => {
    const [format, setFormat] = useState<KartenFormat>(KARTEN_FORMAT_VORGABE);
    const [druckart, setDruckart] = useState<KartenDruckart>(KARTEN_DRUCKART_VORGABE);
    const [erzeugt, setErzeugt] = useState(false);
    const [fehler, setFehler] = useState('');
    const [fertig, setFertig] = useState('');

    const origin = typeof window !== 'undefined' ? window.location.origin : '';

    const erstellen = async () => {
        if (erzeugt || karten.length === 0) return;
        setErzeugt(true);
        setFehler('');
        setFertig('');
        try {
            const { erzeugeMaterialKartenPdf } = await import('../../../services/material/materialKartePdf');
            const blob = await erzeugeMaterialKartenPdf(karten, { format, druckart, origin });
            const dateiname = kartenDateiname(format, druckart);
            await dateiAusgeben(blob, dateiname);
            setFertig(`${dateiname} erstellt.`);
        } catch (e) {
            setFehler(`PDF nicht erstellt: ${describeError(e)}`);
        } finally {
            setErzeugt(false);
        }
    };

    const schliessen = () => { if (!erzeugt) onSchliessen(); };
    const namen = karten.map(k => k.name);

    return (
        <ModalShell
            title="Karten drucken"
            width="max-w-lg"
            onClose={schliessen}
            footer={(
                <>
                    <Button variant="ghost" onClick={schliessen} disabled={erzeugt}>Schließen</Button>
                    <Button
                        icon={<Printer className="w-4 h-4" />}
                        loading={erzeugt}
                        disabled={karten.length === 0}
                        onClick={() => { void erstellen(); }}
                    >
                        PDF erstellen
                    </Button>
                </>
            )}
        >
            <div className="text-sm text-slate-700 space-y-1">
                <p className="font-semibold">{bogenText(karten.length, druckart)}</p>
                <p className="text-xs text-slate-500 break-words">
                    {namen.slice(0, NAMEN_MAX).join(', ')}
                    {namen.length > NAMEN_MAX ? ` + ${namen.length - NAMEN_MAX} weitere` : ''}
                </p>
            </div>

            <Wahl<KartenFormat> name="material-format" titel="Format" wahlen={KARTEN_FORMATE} wert={format} gesperrt={erzeugt} onWahl={setFormat} />
            <Wahl<KartenDruckart> name="material-druckart" titel="Druckart" wahlen={KARTEN_DRUCKARTEN} wert={druckart} gesperrt={erzeugt} onWahl={setDruckart} />

            <p className="text-[11px] text-slate-500 break-all">
                Die QR-Codes führen zu {origin}/m/… — Karten aus einer Testumgebung melden auch dorthin.
            </p>

            {fertig && (
                <p className="flex items-center gap-2 text-xs text-emerald-700">
                    <CheckCircle2 className="w-4 h-4 shrink-0" /> {fertig}
                </p>
            )}
            {fehler && <p role="alert" className="text-xs text-red-700">{fehler}</p>}
        </ModalShell>
    );
};
